/* Zoeken en openen van een project, van overal: een knop (projectpagina) of een
 * zoekveld (dashboard). Met `sneltoets` opent / hem, buiten invoervelden. */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { getStageConfig } from '@/components/crm/project/projectUtils';
import { cn } from '@/lib/utils';
import { useProjectenKort } from './hooks';

export function ProjectZoeker({ vorm = 'knop', sneltoets = false, className }: { vorm?: 'knop' | 'veld'; sneltoets?: boolean; className?: string }) {
  const navigate = useNavigate();
  const kort = useProjectenKort();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!sneltoets) return;
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement;
      if (doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '/') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [sneltoets]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {vorm === 'veld' ? (
          <button
            type="button"
            className={cn('flex h-9 w-full items-center gap-2 rounded-md border bg-background px-3 text-sm text-muted-foreground hover:border-foreground/30', className)}
          >
            <Search className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1 truncate text-left">Ga naar project…</span>
            {sneltoets && <kbd className="rounded border bg-muted px-1.5 font-mono text-[11px]">/</kbd>}
          </button>
        ) : (
          <Button variant="ghost" size="sm" className={cn('h-8 gap-1.5 px-2 text-muted-foreground', className)} aria-label="Ander project kiezen">
            <ChevronsUpDown className="h-4 w-4" />
            <span className="hidden sm:inline">Ander project</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align={vorm === 'veld' ? 'end' : 'start'} className="w-[340px] p-0">
        <Command>
          <CommandInput placeholder="Zoek op nummer of naam…" />
          <CommandList>
            <CommandEmpty>Geen project gevonden.</CommandEmpty>
            <CommandGroup>
              {(kort.data ?? []).map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.project_number ?? ''} ${p.name}`}
                  onSelect={() => {
                    setOpen(false);
                    navigate(`/crm/projects/${p.id}`);
                  }}
                  className="gap-2"
                >
                  <span className="w-10 shrink-0 font-mono text-xs text-muted-foreground">{p.project_number ?? '—'}</span>
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{getStageConfig(p.stage).label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
